import {
    SaveEmission,
    SaveEmissionDone,
    StartEmission,
    StartEmissionDone,
} from "../redux/emission/actions/default";
import { concat, Observable, of } from "rxjs";
import { EmissionActions } from "../redux/emission/actions";
import { EmissionApi } from "../api/emission-api";
import { delay, flatMap, mergeMap } from "rxjs/operators";
import { EmissionValues } from "../containers/quotation/types/emission";
import { TypeRates } from "../constants/enums/quotation";
import {
    CURRENCY_CODES_VALUES,
    ISSUANCE_DATA,
    PACIFICO_ONLINE,
    PRODUCT_NAME,
    QUEUE_ERROR,
    SAFETYPAY_DATA,
} from "../constants/enums/emission";
import { DOCUMENT_TYPES } from "../constants/enums/document-types";
import {
    PageNavigateTo,
    PageOpenGenericErrorModal,
} from "../redux/page/actions/default";
import { ROUTE_APP_SUMMARY } from "../constants/routes/app";
import {
    HideQuotationLoading,
    SaveQuotation,
} from "../redux/quotation/actions/default";
import { COD_APPLICATION } from "../constants/payroll-file";
import { catchErrorWithDispatch } from "../libs/utils/errors";
import { QuotationPaymentOpenPopup } from "../redux/quotation-payment/actions/default";
import { ZohoPhaseFour, ZohoPhaseThree } from "../redux/zoho/actions/default";
import { RootState } from "../redux/reducers";
import { LegalRepresentativePopupOpenPopup } from "../redux/legal-representative-popup/actions/default";
import { LegalRepresentativePopupActions } from "../redux/legal-representative-popup/actions";

export class EmissionRepository {
    static StartEmission(action: StartEmission): Observable<EmissionActions> {
        const numberQuotes = EmissionRepository.getNumberQuotes(action.payload);
        return EmissionApi.startEmission({
            ...action.payload,
            numberQuotes,
        }).pipe(
            delay(2000),
            mergeMap(({ response }) =>
                concat(
                    of(
                        new StartEmissionDone({
                            ...response,
                            numberQuotes,
                        }).create()
                    ),
                    of(new HideQuotationLoading().create()),
                    of(new PageNavigateTo(ROUTE_APP_SUMMARY).create()),
                    of(new ZohoPhaseThree().create())
                )
            ),
            catchErrorWithDispatch((err) =>
                concat(
                    of(new HideQuotationLoading().create()),
                    of(new StartEmissionDone(err).create())
                )
            )
        );
    }

    static SaveEmission(
        action: SaveEmission,
        state: Partial<RootState>
    ): Observable<EmissionActions> {
        const {
            consultQuotation: {
                typeStreet,
                nameStreet,
                numberStreet,
                codeCiiu,
                currencyCode,
                codeProduct,
                codeDepartament,
                codeDistrict,
                codeProvince,
                numberQuote,
                rate,
                nameBusinessBame,
                nameLegalRepresentative,
                numberDocumentLegalRepresentative,
            },
            calculated,
            paymentRequestCode,
            primeSelected,
            userEmail,
            fileName,
            ruc,
            requestNumber,
            phone,
            initialMonth,
            naturalName,
            naturalLastName,
            naturalMotherLastName,
            isNaturalPerson,
            quoteId,
            distributionCode,
        } = action.payload;

        const {
            name: nameRl,
            firstSurname: firstSurnameRl,
            lastSurname: lastSurnameRl,
            documentNumber: numberDoumentRl,
        } = state?.legalRepresentativePopup?.legalRepresentative || {
            nameRl: "",
            firstSurnameRl: "",
            lastSurnameRl: "",
            numberDoumentRl: "",
        };

        const fullNameRl = `${nameRl} ${firstSurnameRl} ${lastSurnameRl}`;

        const selectedMonth = initialMonth.split("/");
        const dateStartTerm = new Date(
            Date.UTC(
                Number.parseInt(selectedMonth[1], 10),
                Number.parseInt(selectedMonth[0], 10) - 1,
                1
            )
        ).toISOString();

        const typeDocLR =
            state?.emission?.startIssuance?.consultQuotation
                ?.typeDocumentLegalRepresentative || DOCUMENT_TYPES.DNI;

        const body: EmissionValues = {
            ageRangeList:
                calculated != null
                    ? calculated[primeSelected].ageRangeList
                    : null,
            address: `${typeStreet} ${nameStreet} ${numberStreet}`,
            ciiu: codeCiiu,
            codeCurrency: CURRENCY_CODES_VALUES[currencyCode],
            codePackageAdditional: ISSUANCE_DATA.CODE_PACKAGE_ADDITIONAL,
            codeProduct: Number.parseInt(codeProduct, 10),
            codeSafetyPay: paymentRequestCode,
            commercialExecutive: PACIFICO_ONLINE,
            dateEmission: new Date().toISOString(),
            dateSale: new Date().toISOString(),
            dateStartTerm,
            departament: codeDepartament,
            district: codeDistrict,
            emailBroker: "broker@marsh.com",
            emailElectronicPolicy: userEmail,
            emailInvoicing: userEmail,
            flagRma: 0,
            inclusionChargeType: "001",
            insuredPayroll: fileName,
            lastName: naturalLastName || "",
            name: naturalName || "",
            nameBroker: "Marsh Seguros",
            nameProduct: PRODUCT_NAME,
            nameRl: fullNameRl.trim() ? fullNameRl : nameLegalRepresentative,
            numberDocument: ruc,
            numberDocumentBroker: ISSUANCE_DATA.BROKER_DOCUMENT_NUMBER,
            numberDocumentRl:
                numberDoumentRl || numberDocumentLegalRepresentative,
            numberRequest: requestNumber,
            paymentMethod: primeSelected === TypeRates.ANNUAL ? 12 : 6,
            paymentWithSafetyPay: true,
            province: codeProvince,
            quoteNumber: numberQuote,
            rate,
            safetyPayData: {
                apiKey: SAFETYPAY_DATA.API_KEY,
                applicationId: 4,
                expirationTime: SAFETYPAY_DATA.EXPIRATION_TIME,
                merchantSalesId: paymentRequestCode,
                signatureKey: SAFETYPAY_DATA.SIGNATURE_KEY,
                transactionErrorUrl: "https://www.pacifico.com.pe",
                transactionOkUrl: "https://www.pacifico.com.pe",
            },
            secondLastName: naturalMotherLastName || "",
            socialReason: isNaturalPerson
                ? `${naturalMotherLastName} ${naturalLastName}, ${naturalName}`
                : nameBusinessBame,
            telephone: Number.parseInt(phone, 10),
            typeDocument: "004",
            typeDocumentBroker: ISSUANCE_DATA.BROKER_TYPE_DOCUMENT_NUMBER,
            typeDocumentRl: typeDocLR === DOCUMENT_TYPES.CE ? "003" : "001",
            codigoaplicacion: COD_APPLICATION,
            quoteId,
            distributionCode,
        };
        return EmissionApi.saveEmission(body).pipe(
            flatMap((response) => {
                const { status } = response;

                if (status === QUEUE_ERROR) {
                    return concat(
                        of(new PageOpenGenericErrorModal().create()),
                        of(
                            new SaveEmissionDone(
                                new Error("Queue error")
                            ).create()
                        )
                    );
                }

                return concat(
                    of(new SaveEmissionDone(response).create()),
                    of(new QuotationPaymentOpenPopup().create()),
                    of(new ZohoPhaseFour().create())
                );
            }),
            catchErrorWithDispatch((err) =>
                concat(
                    of(new HideQuotationLoading().create()),
                    of(new SaveEmissionDone(err).create())
                )
            )
        );
    }

    static verifyIsLegalRepresentative(
        response: {
            idTransaction: number;
            quoteCode: string;
        },
        rucHasLegalRepresentative: boolean
    ): Observable<LegalRepresentativePopupActions> {
        const numberQuotes = EmissionRepository.getNumberQuotes(response);

        return EmissionApi.startEmission({
            ...response,
            numberQuotes,
        }).pipe(
            mergeMap(({ response }) => {
                const typeDocLR =
                    response?.consultQuotation
                        ?.typeDocumentLegalRepresentative || DOCUMENT_TYPES.DNI;

                if (
                    (typeDocLR != DOCUMENT_TYPES.DNI &&
                        typeDocLR != DOCUMENT_TYPES.CE) ||
                    !rucHasLegalRepresentative
                ) {
                    return of(
                        new HideQuotationLoading().create(),
                        new LegalRepresentativePopupOpenPopup().create()
                    );
                }

                return of(new SaveQuotation().create());
            })
        );
    }

    static getNumberQuotes(data: { idTransaction: number; quoteCode: string }) {
        const { quoteCode } = data;
        const numberQuotes = Number.parseInt(quoteCode.split("-")[1], 10);
        return numberQuotes;
    }
}
